import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface DeveloperSession {
  token: string;
  expiresAt: string;
  targetUserId: string;
  targetOrganizationId?: string;
  accessMode: 'ims' | 'cashbook';
}

interface DeveloperContextType {
  isDeveloperMode: boolean;
  developerSession: DeveloperSession | null;
  startSession: (userId: string, mode: 'ims' | 'cashbook', duration?: number) => Promise<void>;
  endSession: () => Promise<void>;
  logPageView: (path: string) => void;
  logAction: (actionType: string, details?: any) => void;
}

const DeveloperContext = createContext<DeveloperContextType | undefined>(undefined);

export function DeveloperProvider({ children }: { children: ReactNode }) {
  const [isDeveloperMode, setIsDeveloperMode] = useState(false);
  const [developerSession, setDeveloperSession] = useState<DeveloperSession | null>(null);

  useEffect(() => {
    // Check for existing session in localStorage
    const storedSession = localStorage.getItem('developer_session');
    if (storedSession) {
      const session = JSON.parse(storedSession);
      // Validate session with backend
      validateSession(session.token);
    }
  }, []);

  const validateSession = async (token: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('developer-access', {
        body: { sessionToken: token },
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (error) throw error;

      if (data.isValid && data.session) {
        setDeveloperSession({ token, ...data.session });
        setIsDeveloperMode(true);
      } else {
        // Session expired or invalid
        localStorage.removeItem('developer_session');
        setDeveloperSession(null);
        setIsDeveloperMode(false);
      }
    } catch (error) {
      console.error('Session validation error:', error);
      localStorage.removeItem('developer_session');
      setDeveloperSession(null);
      setIsDeveloperMode(false);
    }
  };

  const startSession = async (userId: string, mode: 'ims' | 'cashbook', duration: number = 30) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `https://hswpjvekpojgeyuykpiq.supabase.co/functions/v1/developer-access?action=create_session`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            targetUserId: userId,
            accessMode: mode,
            durationMinutes: duration
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create session');
      }

      if (data.success && data.session) {
        setDeveloperSession(data.session);
        setIsDeveloperMode(true);
        localStorage.setItem('developer_session', JSON.stringify(data.session));
      }
    } catch (error) {
      console.error('Failed to start developer session:', error);
      throw error;
    }
  };

  const endSession = async () => {
    if (!developerSession) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      await fetch(
        `https://hswpjvekpojgeyuykpiq.supabase.co/functions/v1/developer-access?action=end_session`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionToken: developerSession.token
          })
        }
      );

      localStorage.removeItem('developer_session');
      setDeveloperSession(null);
      setIsDeveloperMode(false);
    } catch (error) {
      console.error('Failed to end developer session:', error);
    }
  };

  const logPageView = (path: string) => {
    if (!developerSession || !isDeveloperMode) return;

    logAction('page_view', { page_path: path });
  };

  const logAction = async (actionType: string, details?: any) => {
    if (!developerSession || !isDeveloperMode) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await fetch(
        `https://hswpjvekpojgeyuykpiq.supabase.co/functions/v1/developer-access?action=log_activity`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionToken: developerSession.token,
            actionType,
            pagePath: window.location.pathname,
            ...details
          })
        }
      );
    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  };

  return (
    <DeveloperContext.Provider
      value={{
        isDeveloperMode,
        developerSession,
        startSession,
        endSession,
        logPageView,
        logAction,
      }}
    >
      {children}
    </DeveloperContext.Provider>
  );
}

export function useDeveloper() {
  const context = useContext(DeveloperContext);
  if (context === undefined) {
    throw new Error('useDeveloper must be used within a DeveloperProvider');
  }
  return context;
}
